import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listRecruiterInvites, type InviteOut, type RecruiterInvitesResponse } from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { DataTable, type DataTableColumn } from '@/components/spectrumui/data-table'
import { formatDate, recBadge } from '@/lib/dashboard-utils'
import {
  groupCompletedByTitle,
  orderRowsForFeed,
  recommendationRank,
  sectionColumns,
} from '@/lib/compare-utils'
import { usePageMeta } from '@/hooks/use-page-meta'

/**
 * Stack-ranking for completed interviews: pick a role (invites grouped by
 * identical title) and compare its candidates in one sortable table, instead
 * of clicking through a detail page per candidate.
 */
export function RecruiterCompare() {
  usePageMeta('Compare candidates — Aura Recruiter')
  const navigate = useNavigate()
  const [data, setData] = useState<RecruiterInvitesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Null = no explicit pick yet; the largest group leads by default.
  const [pickedTitle, setPickedTitle] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setData(null)
    setError(null)
    listRecruiterInvites()
      .then(d => { if (!cancelled) setData(d) })
      .catch(err => {
        if (cancelled) return
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        setError('Failed to load your interviews. Please try again.')
      })
    return () => { cancelled = true }
  }, [navigate])

  const groups = useMemo(() => groupCompletedByTitle(data?.invites ?? []), [data])
  // The issue's partial state: with a single completed interview there is
  // nothing to rank against, so even a formed group of one stays hidden.
  const completedCount = useMemo(
    () => (data?.invites ?? []).filter(i => i.status === 'completed').length,
    [data],
  )
  const activeGroup = groups.find(g => g.title === pickedTitle) ?? groups[0] ?? null
  const rows = useMemo(() => (activeGroup ? orderRowsForFeed(activeGroup.invites) : []), [activeGroup])
  const sections = useMemo(() => sectionColumns(activeGroup?.invites ?? []), [activeGroup])

  const columns: DataTableColumn<InviteOut>[] = useMemo(() => [
    {
      id: 'candidate',
      header: 'Candidate',
      value: i => i.candidate_name ?? '',
      cell: i => i.candidate_name ?? '—',
    },
    {
      id: 'overall',
      header: 'Overall',
      numeric: true,
      sortable: true,
      value: i => i.overall_score ?? undefined,
      cell: i => i.overall_score ?? '—',
    },
    // One column per section any report in the group carries; a candidate
    // without that section sorts as a blank (last) rather than a fake 0.
    ...sections.map(name => ({
      id: `section:${name}`,
      header: name,
      numeric: true,
      sortable: true,
      hideBelow: 'md' as const,
      value: (i: InviteOut) => i.section_scores?.[name] ?? undefined,
      cell: (i: InviteOut) => i.section_scores?.[name] ?? '—',
    })),
    {
      id: 'recommendation',
      header: 'Recommendation',
      sortable: true,
      value: i => recommendationRank(i.recommendation) ?? undefined,
      cell: i => {
        const rec = recBadge(i.recommendation)
        return <span className={rec.cls}>{rec.text}</span>
      },
      hideBelow: 'sm',
    },
    {
      id: 'integrity',
      header: 'Flags',
      numeric: true,
      sortable: true,
      // Not proctored = never recorded, so "—". A proctored invite with no
      // flushed events really did record zero flags, so it shows 0.
      value: i => (i.proctoring ? i.integrity_flags ?? 0 : undefined),
      cell: i => (i.proctoring ? i.integrity_flags ?? 0 : '—'),
      hideBelow: 'md',
    },
    {
      id: 'completed',
      header: 'Completed',
      sortable: true,
      // Epoch (not the formatted string) is what sorting compares.
      value: i => new Date(i.completed_at ?? i.created_at).getTime(),
      cell: i => formatDate(i.completed_at ?? i.created_at),
      hideBelow: 'md',
    },
  ], [sections])

  return (
    <AppShell active="compare">
      {error && <div className="wbanner" role="alert">{error}</div>}

      {data === null && !error ? (
        <div className="wloading" role="status" aria-live="polite">
          <span className="wspinner" aria-hidden="true"></span>
          <span>Loading interviews…</span>
        </div>
      ) : completedCount < 2 ? (
        // Fewer than two completed interviews: there is nothing to rank.
        <section className="panel">
          <div className="empty">
            <h3>Nothing to compare yet</h3>
            <p>Compare stack-ranks the candidates who finished the same interview. Once at least two of your invites are completed, their reports line up here — scores, sections, and recommendations side by side.</p>
            <div className="empty-cta flex items-center justify-center gap-2">
              <Link to="/recruiter" className="wbtn wbtn-secondary">Back to dashboard</Link>
              <Link to="/recruiter/new" className="wbtn wbtn-primary">New interview</Link>
            </div>
          </div>
        </section>
      ) : (
        <>
          <header className="screen-head">
            <div>
              <h1>Compare candidates</h1>
              <p>Pick a role to stack-rank everyone who completed it. Open a row for the full report, transcript, and recording.</p>
            </div>
          </header>

          <div className="wfilters" role="group" aria-label="Role to compare">
            {groups.map(g => (
              <button
                key={g.title}
                type="button"
                className="chip"
                aria-pressed={g.title === activeGroup?.title}
                onClick={() => setPickedTitle(g.title)}
              >
                {g.title} · {g.invites.length}
              </button>
            ))}
          </div>

          <DataTable
            data={rows}
            columns={columns}
            rowId={i => i.invite_id}
            rowLabel={i => `Report for ${i.candidate_name ?? 'candidate'}`}
            caption={`Completed interviews for ${activeGroup?.title ?? ''}, ranked`}
            variant="panel"
            defaultSort={{ columnId: 'overall', direction: 'desc' }}
            onRowClick={i => navigate(`/recruiter/invites/${i.invite_id}`)}
          />
        </>
      )}
    </AppShell>
  )
}
